// Main component
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useOverview } from "../../hooks";
import { Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Input } from "../ui/input";
import Loader from "../Loader/PageLoader";

// Separate component files
import SelfAccountCard from "./SelfAccountCard";
import BeneficiaryList from "./BeneficiaryList";
import StickyButton from "./StickyButton";

function SendMoney({
  setStateVariable,
  stateVariable,
  setBeneficiarySelected,
  setCurrentFlow,
}) {
  const { useBeneficiaryDetails, setInvestmentAccount, setIsHedgeFundAccount } =
    useOverview();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef(null);
  const addPayeeRef = useRef(null);

  // Data fetching
  const { data: beneficiaryData, isLoading: beneficiaryDataIsLoading } =
    useBeneficiaryDetails({ status: undefined });

  // Approved payees plus those still under manual review (shown greyed out)
  const allBeneficiaries = useMemo(() => {
    return (beneficiaryData?.data?.beneficiaries || []).filter((b) =>
      ["APPROVED", "MANUAL_REVIEW"].includes(b?.status?.trim()?.toUpperCase())
    );
  }, [beneficiaryData]);

  const beneficiaries = useMemo(
    () =>
      allBeneficiaries.filter(
        (b) => b?.status?.trim()?.toUpperCase() === "APPROVED"
      ),
    [allBeneficiaries]
  );

  // Derived state
  const hasMultipleBeneficiaries = allBeneficiaries.length > 0;

  // Handler functions
  const handleAccordionChange = (value) => {
    if (value === "payees") {
      setTimeout(checkButtonVisibility, 300);
    } else {
      setIsSticky(false);
    }
  };

  const checkButtonVisibility = () => {
    if (!addPayeeRef.current) return;

    const buttonRect = addPayeeRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    setIsSticky(buttonRect.bottom > viewportHeight);
  };

  const handleSelfAccountSelect = (data) => {
    if (data?.beneType === "INVESTING") {
      setStateVariable(stateVariable + 1);
      setInvestmentAccount(true);
      setIsHedgeFundAccount("");
      setBeneficiarySelected(data);
    } else if (data?.beneType === "HEDGEFUND") {
      setStateVariable(stateVariable + 1);
      setIsHedgeFundAccount(true);
      setInvestmentAccount("");
      setBeneficiarySelected(data);
    }
    else{
       setStateVariable(stateVariable + 1);
         setBeneficiarySelected(data);
    }
  };

  const handleBeneficiarySelect = (beneficiary) => {
    setBeneficiarySelected(beneficiary);
    setStateVariable(stateVariable + 1);
    // setSelectedCurrency(beneficiary);
  };

  const handleAddNewPayee = () => {
    setStateVariable(1);
    setCurrentFlow("addNewPayee");
  };

  // Group sibling records (same payee, different method) into one row.
  // Each row carries `methods: { LOCAL: status, SWIFT: status }`; the row
  // selects through its first APPROVED record (none => row is not selectable).
  const filteredBeneficiaries = useMemo(() => {
    if (!allBeneficiaries.length) return [];

    const groups = new Map();

    allBeneficiaries.forEach((user) => {
      // Exclude "INVESTMENT" type beneficiaries
      if (user.beneType === "INVESTING" || user.beneType === "HEDGEFUND")
        return;

      if (
        searchTerm.length > 0 &&
        !user?.nameOfBeni?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return;
      }

      // Key based on name, country, and IBAN/account info
      const key = `${user.nameOfBeni}-${user.country}-${user.iban || user.maskAccountNo}`;
      const status = user?.status?.trim()?.toUpperCase();
      const method = (
        user?.method ||
        user?.beniConfigCountry?.method ||
        ""
      ).toUpperCase();

      const group = groups.get(key) || { representative: null, methods: {} };
      if (method && group.methods[method] !== "APPROVED") {
        group.methods[method] = status;
      }
      if (
        !group.representative ||
        (status === "APPROVED" &&
          group.representative.status?.trim()?.toUpperCase() !== "APPROVED")
      ) {
        group.representative = user;
      }
      groups.set(key, group);
    });

    return Array.from(groups.values()).map(({ representative, methods }) => ({
      ...representative,
      methods,
    }));
  }, [allBeneficiaries, searchTerm]);

  // Scroll effect
  useEffect(() => {
    window.addEventListener("scroll", checkButtonVisibility);
    return () => window.removeEventListener("scroll", checkButtonVisibility);
  }, []);

  // Loading state
  if (beneficiaryDataIsLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader className="h-20 w-20" />
      </div>
    );
  }

  const getBeneTypeTrue = (arr) => {
    if (!arr || arr.length === 0) return null;

    // Filter out items with null beneType
    const filteredArr = arr.filter((item) => item.beneType !== null);

    // Create a Map to track unique combinations of maskAccountNo and beneType
    const uniqueMap = new Map();

    filteredArr.forEach((item) => {
      const key = `${item.maskAccountNo}-${item.beneType}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, item);
      }
    });

    // Return array of unique items
    return Array.from(uniqueMap.values());
  };

  const result = getBeneTypeTrue(beneficiaries);

  const hasBeneficiaries = result;

  return (
    <div className="grid py-6 gap-4 relative" ref={containerRef}>
      {hasBeneficiaries?.length > 0 && (
        <SelfAccountCard
          selfAccount={result}
          onSelect={handleSelfAccountSelect}
        />
      )}

      {hasMultipleBeneficiaries && (
        <Accordion
          type="single"
          collapsible
          className="w-full"
          onValueChange={handleAccordionChange}
        >
          <AccordionItem value="payees">
            <AccordionTrigger>Your Payees</AccordionTrigger>
            <AccordionContent>
              <div className="relative top-0 z-10 bg-white pb-2 mt-4">
                <Input
                  placeholder="Search here"
                  className="pl-4"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute right-5 top-3 h-4 w-4 text-muted-foreground" />
              </div>
              <BeneficiaryList
                beneficiaries={filteredBeneficiaries}
                onSelect={handleBeneficiarySelect}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <StickyButton
        ref={addPayeeRef}
        isSticky={isSticky}
        containerWidth={containerRef.current?.clientWidth}
        onClick={handleAddNewPayee}
        label="Add new Payee"
      />

      {isSticky && <div className="h-16"></div>}
    </div>
  );
}

export default SendMoney;
